import { useEffect, useCallback } from "react";
import { useAtomValue, useSetAtom, useStore } from "jotai";
import {
  warningAlertStateAtom,
  WarningAlertState,
  isAiPageAtom,
  hasZslAtom,
  canvasIdAtom,
  programmaticNavigationExpectedAtom,
} from "@zapier/canvas-state";

const defaultWarningAlertState: WarningAlertState = {
  isOpen: false,
  title: "",
  message: "",
  primaryButtonText: "Confirm",
  secondaryButtonText: "Cancel",
  status: "warning",
  onPrimaryAction: () => {},
  onSecondaryAction: undefined,
  isPrimaryLoading: false,
};

export function useUnsavedChangesWarning() {
  const isAiPage = useAtomValue(isAiPageAtom);
  const hasZsl = useAtomValue(hasZslAtom);
  const canvasId = useAtomValue(canvasIdAtom);
  const store = useStore();
  const setProgrammaticNavExpected = useSetAtom(
    programmaticNavigationExpectedAtom,
  );
  const setWarningAlertState = useSetAtom(warningAlertStateAtom);

  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      const isExpected = store.get(programmaticNavigationExpectedAtom);

      if (isExpected) {
        setTimeout(() => {
          setProgrammaticNavExpected(false);
        }, 50);
        return;
      }

      event.preventDefault();
      event.returnValue = "";
      return "";
    },
    [store, setProgrammaticNavExpected],
  );

  useEffect(() => {
    const shouldAddListener = isAiPage || (hasZsl && canvasId);
    if (!shouldAddListener) {
      return;
    }

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isAiPage, hasZsl, canvasId, handleBeforeUnload]);

  const closeWarning = useCallback(() => {
    setWarningAlertState(defaultWarningAlertState);
  }, [setWarningAlertState]);

  const requestStartOverWarning = useCallback(
    (confirmAction: () => void | Promise<void>) => {
      if (!isAiPage) {
        confirmAction();
        return;
      }

      const handlePrimaryAction = async () => {
        setWarningAlertState((prev) => ({ ...prev, isPrimaryLoading: true }));

        setTimeout(async () => {
          try {
            await Promise.resolve(confirmAction());
          } catch (error) {
            setProgrammaticNavExpected(false);
          } finally {
            closeWarning();
          }
        }, 0);
      };

      setWarningAlertState({
        isOpen: true,
        title:
          "You will lose your generated canvas and any progress from this session if you continue.",
        message: "Do you wish to continue?",
        primaryButtonText: "Start from scratch",
        secondaryButtonText: "Cancel",
        status: "warning",
        onPrimaryAction: handlePrimaryAction,
        onSecondaryAction: closeWarning,
        isPrimaryLoading: false,
      });
    },
    [isAiPage, setWarningAlertState, closeWarning, setProgrammaticNavExpected],
  );

  return { requestStartOverWarning, closeWarning };
}
